// src/components/ui/Input/Input.jsx
import React, { forwardRef, useId } from 'react';

export const Input = forwardRef(({
  label,
  error,
  className = '',
  fullWidth = true,
  id,
  ...props
}, ref) => {
  // ============================================================
  // 🔑 `useId` СВЯЗЫВАЕТ ПОДПИСЬ С ПОЛЕМ — И ЭТО НЕ ФОРМАЛЬНОСТЬ.
  //
  //    Раньше <label> и <input> были просто соседями: у label нет
  //    `htmlFor`, у input нет `id`. Последствия:
  //      • клик по подписи НЕ ставит курсор в поле;
  //      • скринридер читает поле как «поле ввода» без названия —
  //        форма из восьми полей становится неозвучиваемой;
  //      • автотесты не находят поле по подписи (проверено: мой
  //        собственный тест оформления заказа упал именно на этом).
  //
  //    ⚠️ `useId` даёт стабильный id даже при нескольких формах на
  //       странице. Прописывать id вручную нельзя: два Input с
  //       одинаковым id сломали бы связь — браузер взял бы первый.
  //    ⚠️ Явно переданный `id` имеет приоритет: он нужен, когда поле
  //       связывают с чем-то извне.
  // ============================================================
  const autoId = useId();
  const inputId = id || autoId;
  const errorId = `${inputId}-error`;

  const inputStyle = {
    width: fullWidth ? '100%' : 'auto',
    padding: '10px 16px',
    // Границы — из токенов: светло-серая рамка была бы невидима
    // на тёмном фоне.
    border: `1px solid ${error ? 'rgb(var(--c-danger))' : 'rgb(var(--c-border))'}`,
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    // 🔴 Фон ПОЛЯ — `surface`, а не `bg` и не `on-accent`.
    //    `on-accent` — токен ТЕКСТА на акценте (в тёмной теме почти
    //    чёрный): поле получило бы чёрный фон.
    //    `bg` — фон СТРАНИЦЫ: поле слилось бы с фоном, и граница
    //    выглядела бы висящей в воздухе.
    backgroundColor: 'rgb(var(--c-surface))',
    color: 'rgb(var(--c-text))',
    fontFamily: 'inherit',
  };

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto' }}>
      {label && (
        <label
          htmlFor={inputId}
          style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'rgb(var(--c-text))',
          }}
        >
          {label}
        </label>
      )}
      <input
        id={inputId}
        ref={ref}
        style={inputStyle}
        className={className}
        // Связь с текстом ошибки для скринридеров: без неё пользователь
        // не узнает, ПОЧЕМУ поле подсвечено.
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? errorId : undefined}
        {...props}
      />
      {error && (
        <p
          id={errorId}
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: 'rgb(var(--c-danger))',
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';
